<script setup lang="ts">
import { usePictograms } from "@/composables/usePictograms";
import { FileAcceptTypes, FileSizes } from "@/enums";
import type { IFileState, IMultiSelectItem } from "@/interfaces";
import { usePlannerTemplateForm } from "@/stores/plannerTemplateFormStore";
import SimpleImagePreview from "@/stories/displays/FileGallery/SimpleImagePreview.vue";
import FileInput from "@/stories/form/FileInput/FileInput.vue";
import ImageCheckbox from "@/stories/form/ImageCheckbox/ImageCheckbox.vue";
import TextInput from "@/stories/form/TextInput/TextInput.vue";
import FlexRow from "@/stories/layout/FlexRow/FlexRow.vue";
import FormContent from "@/views/planner/dialogs/templates/form/FormContent.vue";
import { computed } from "vue";

const store = usePlannerTemplateForm();
const { pictogramsList } = usePictograms();

const isSearching = computed(() => store.searchText.trim().length);

const pictograms = computed(() => {
  if (!isSearching.value) {
    return pictogramsList.value;
  }

  const text = store.searchText.toLocaleLowerCase();
  return pictogramsList.value
    .filter((item) => item.title.toLocaleLowerCase().includes(text))
    .slice(0, 15);
});

const handlePictogramSelection = (source?: IMultiSelectItem) => {
  store.imageToUpload = [];
  if (store.form.pictogram === source) {
    store.form.pictogram = undefined;
  } else {
    store.form.pictogram = source;
  }
};

const handleLocalImageSelection = (files?: IFileState[]) => {
  store.form.pictogram = undefined;
  if (files) {
    store.imageToUpload = files;
  } else {
    store.imageToUpload = [];
  }
};
</script>

<template>
  <FormContent>
    <div v-if="store.selectedImage" class="pb-4">
      <SimpleImagePreview
        :image="store.selectedImage"
        @remove="store.handleRemoveFile"
      />
    </div>

    <FlexRow gap="lg">
      <div class="h600 text-neutral1000">
        {{ $t("templates.find_or_upload_icon") }}
      </div>
      <TextInput
        name="search"
        rounded
        size="sm"
        icon="fas-magnifying-glass"
        background="bg-neutral100"
        :placeholder="$t('media.search_pictogram')"
        v-model="store.searchText"
      />
    </FlexRow>

    <div v-if="isSearching" class="mt-5">
      <div v-if="pictograms.length === 0">
        {{ $t("global.noData") }}
      </div>
      <div class="grid grid-cols-5 grid-rows-3 gap-4">
        <div v-for="pictogram of pictograms" :key="pictogram.id">
          <ImageCheckbox
            v-if="pictogram.imageURL"
            :src="pictogram.imageURL"
            :alt="pictogram.title"
            :checked="store.form.pictogram?.id === pictogram.id"
            @toggle="() => handlePictogramSelection(pictogram)"
          />
        </div>
      </div>
    </div>

    <div v-if="!isSearching" class="pt-8">
      <FileInput
        name="image"
        :maxFile="1"
        :maxSize="FileSizes.Max15MB"
        :accept="FileAcceptTypes.OnlyImage"
        :validation="store.validation"
        :imageGalleryEnabled="false"
        :modelValue="store.imageToUpload"
        :autoHideTrigger="false"
        autoReplace
        @update:modelValue="handleLocalImageSelection"
      />
    </div>
  </FormContent>
</template>
